// Prompt: study-type disambiguation for ambiguous pattern-classifier cases.
// Invoked only when pattern confidence < 0.75 (PRD §7 Feature 2).

import type { ChatRequest } from '../types';
import type { ClassifierLabel, ClassifierResult } from '../../patterns/types';
import type { ManuscriptDocument } from '../../pdf/types';

export const PROMPT_VERSION = 'classify_study-1.0';

const LABELS: ClassifierLabel[] = [
  'randomised controlled trial',
  'cohort study',
  'case-control study',
  'cross-sectional study',
  'systematic review',
  'diagnostic accuracy study',
  'case report',
  'narrative review',
  'editorial',
  'perspective',
  'unclassified',
];

const SCHEMA = {
  type: 'object',
  additionalProperties: false,
  required: ['label', 'confidence', 'rationale'],
  properties: {
    label: { type: 'string', enum: LABELS },
    confidence: { type: 'number', minimum: 0, maximum: 1 },
    rationale: { type: 'string', minLength: 1, maxLength: 600 },
  },
} as const;

export interface ClassifyStudyOutput {
  label: ClassifierLabel;
  confidence: number;
  rationale: string;
}

function abstractText(doc: ManuscriptDocument): string {
  const abs = doc.sections.find((s) => s.section === 'abstract');
  if (!abs) return '';
  return doc.rawText.slice(abs.start, abs.end).slice(0, 2000);
}

function methodsHead(doc: ManuscriptDocument): string {
  const methods = doc.sections.find((s) => s.section === 'methods');
  if (!methods) return '';
  return doc.rawText.slice(methods.start, methods.end).slice(0, 1500);
}

export function buildPrompt(
  doc: ManuscriptDocument,
  pattern: ClassifierResult,
): ChatRequest {
  const top = pattern.label;
  const alternatives = pattern.alternatives.map((a) => a.label).join(', ');

  const systemPrompt =
    'You are an editorial assistant classifying a medical manuscript by study type. The pattern engine made a low-confidence guess and you must confirm or revise. Choose exactly one label from the enum. Confidence must reflect how clearly the manuscript fits that label.';

  const userPrompt = `Title:\n${doc.title}\n\nAbstract:\n${abstractText(doc) || '(no abstract section detected)'}\n\nMethods (truncated):\n${methodsHead(doc) || '(no methods section detected)'}\n\nPattern engine's guess: "${top}" (confidence ${(pattern.confidence * 100).toFixed(0)}%). Alternatives it considered: ${alternatives || 'none'}.\n\nReturn JSON: { "label": <one of the enum>, "confidence": <0..1>, "rationale": <≤2 sentences> }`;

  return {
    systemPrompt,
    userPrompt,
    schema: SCHEMA,
    maxTokens: 300,
    promptVersion: PROMPT_VERSION,
  };
}

export function parseResponse(json: unknown): ClassifyStudyOutput | null {
  if (typeof json !== 'object' || json === null) return null;
  const o = json as Record<string, unknown>;
  if (
    typeof o.label !== 'string' ||
    typeof o.confidence !== 'number' ||
    typeof o.rationale !== 'string'
  ) {
    return null;
  }
  if (!LABELS.includes(o.label as ClassifierLabel)) return null;
  return {
    label: o.label as ClassifierLabel,
    confidence: o.confidence,
    rationale: o.rationale,
  };
}
